/**
 * Compact user menu — used in the dashboard top edge and the desk top bar.
 *
 * Renders nothing while session is loading (avoids a flash of "anonymous"
 * before the cookie cache resolves). Renders nothing if there's no
 * session at all (the router guard handles redirecting to /login).
 *
 * Keep this lean: avatar (or initial fallback), first name, sign-out
 * trigger. Anything beyond that — switch account, account settings, plan
 * — gets added when there's a real reason to.
 */

import { useQueryClient } from "@tanstack/react-query";
import { Bell, LogOut } from "lucide-react";
import { useState } from "react";
import {
  isDevAuthBypassEnabled,
  logout,
  sessionQueryKey,
  useSession,
} from "@/api/auth-client";
import { useNotificationPrefs } from "@/notifications/app-activity-provider";

export function UserMenu({ compact = false }: { compact?: boolean }) {
  const queryClient = useQueryClient();
  const { data: session, isPending } = useSession();
  const { prefs, permission } = useNotificationPrefs();
  const [signingOut, setSigningOut] = useState(false);
  // Tracks whether the avatar img errored. Some browsers reject
  // googleusercontent.com requests (referrer-policy plugins, ad blockers,
  // CSP overrides). When that happens we fall back to the initial badge
  // — same width/height so the layout doesn't shift.
  const [imgFailed, setImgFailed] = useState(false);

  if (isPending || !session?.user) return null;

  const user = session.user;
  // Better Auth's user type has name + image + email. We may have hooked
  // additional fields (e.g. spend_limit), but those aren't on the
  // session payload by default.
  const firstName = user.name?.trim().split(/\s+/)[0] ?? "";
  const initial = firstName ? firstName[0]!.toUpperCase() : "·";
  const showImage = user.image && !imgFailed;
  const notifLabel =
    permission === "unsupported"
      ? "notifications unsupported"
      : !prefs.desktop
        ? "notifications off"
        : permission === "granted"
          ? "notifications on"
          : permission === "denied"
            ? "notifications blocked"
            : "notifications pending";
  const notifColor =
    permission === "unsupported"
      ? "var(--t4)"
      : !prefs.desktop
        ? "var(--t4)"
        : permission === "granted"
          ? "var(--ok)"
          : "var(--warm)";

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      const result = await logout({
        redirectTo: new URL("/", window.location.origin).toString(),
      });
      await queryClient.invalidateQueries({ queryKey: sessionQueryKey });
      if (result.redirectTo) {
        window.location.assign(result.redirectTo);
        return;
      }
      window.location.assign("/login");
    } catch {
      setSigningOut(false);
    }
  };

  return (
    <div
      className="flex items-center gap-2"
      title={user.email ?? undefined}
      style={{ fontFamily: "var(--font-ui)", fontSize: "var(--text-xs)" }}
    >
      {showImage ? (
        <img
          src={user.image as string}
          alt=""
          // Ask the browser not to send Referer to googleusercontent.com.
          // Google's image CDN doesn't require it, and some browsers /
          // privacy extensions block cross-origin loads when the Referer
          // identifies a private localhost origin. no-referrer is what
          // Google's own products use.
          referrerPolicy="no-referrer"
          width={20}
          height={20}
          style={{
            width: 20,
            height: 20,
            borderRadius: "50%",
            objectFit: "cover",
            display: "block",
            flexShrink: 0,
          }}
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span
          aria-hidden="true"
          className="flex items-center justify-center"
          style={{
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: "var(--accent-light)",
            color: "var(--accent)",
            fontWeight: 600,
            fontSize: "var(--text-2xs)",
            flexShrink: 0,
          }}
        >
          {initial}
        </span>
      )}
      {!compact && (
        <span
          style={{
            color: "var(--t3)",
            maxWidth: 100,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {firstName || user.email}
        </span>
      )}
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
        title={notifLabel}
        style={{
          color: notifColor,
          background: `color-mix(in srgb, ${notifColor} 10%, transparent)`,
          fontFamily: "var(--font-mono)",
          fontSize: compact ? "10px" : "11px",
        }}
      >
        <Bell size={11} />
        {!compact && (
          <span>
            {permission === "granted" && prefs.desktop ? "on" : "off"}
          </span>
        )}
      </span>
      {!isDevAuthBypassEnabled() && (
        <button
          type="button"
          onClick={handleSignOut}
          disabled={signingOut}
          title={`Sign out${user.email ? ` (${user.email})` : ""}`}
          aria-label="Sign out"
          className="p-1 rounded hover:bg-[var(--hover)] transition-colors disabled:opacity-60"
          style={{ color: "var(--t4)" }}
        >
          <LogOut size={13} />
        </button>
      )}
    </div>
  );
}
